import React, { useCallback, useEffect, useMemo, useState } from "react";
import { pagosApi } from "./pagosApi";

/* global BigInt */

export const ESTADO_LABELS = {
  BORRADOR: "Borrador",
  SIMULADO: "Simulado",
  PENDIENTE_APROBACION: "Pendiente de aprobación",
  APROBADO_PARA_PRUEBAS: "Aprobado para pruebas",
  RECHAZADO: "Rechazado",
  REEMPLAZADO: "Reemplazado",
};

const ESTADO_CLASES = {
  APROBADO_PARA_PRUEBAS: "pagos-status--safe",
  PENDIENTE_APROBACION: "pagos-status--pending",
  RECHAZADO: "pagos-status--warning",
};

const FORM_VACIO = {
  tipo: "", alcance: "", sujeto: "", parametro: "", prioridad: "0", porcentaje: "", desde: "", hasta: "", fuente: "", motivo: "",
};

/**
 * Formatea un decimal recibido como texto ("-1234.5") sin pasarlo por Number: el backend es la
 * única fuente de importes y la pantalla no los recalcula ni los redondea.
 */
export const formatDecimal = (texto, { moneda = true, decimales = 2 } = {}) => {
  if (texto === null || texto === undefined || texto === "") return "No disponible";
  const raw = String(texto);
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return raw;
  const negativo = raw.startsWith("-");
  const [entero, fraccion = ""] = raw.replace("-", "").split(".");
  const escala = 10n ** BigInt(decimales);
  const base = BigInt(entero) * escala + BigInt((fraccion.slice(0, decimales) || "0").padEnd(decimales, "0"));
  const redondeado = base + (Number(fraccion[decimales] || "0") >= 5 ? 1n : 0n);
  const canonico = redondeado.toString().padStart(decimales + 1, "0");
  const parteEntera = decimales ? canonico.slice(0, -decimales) : canonico;
  const miles = parteEntera.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const frac = decimales ? canonico.slice(-decimales) : "";
  return `${negativo ? "-" : ""}${moneda ? "$ " : ""}${miles}${frac ? `,${frac}` : ""}`;
};

const formatPorcentaje = (texto) => (texto === null || texto === undefined ? "—" : `${String(texto).replace(".", ",")} %`);
const formatFecha = (value) => {
  const [year, month, day] = String(value || "").slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "Sin fin";
};
const vigencia = (regla) => `${formatFecha(regla.desde)} → ${regla.hasta ? formatFecha(regla.hasta) : "sin fin"}`;
const fechaInput = (value) => (value ? String(value).slice(0, 10) : "");

function Estado({ estado }) {
  return <span className={`pagos-status ${ESTADO_CLASES[estado] || ""}`}>{ESTADO_LABELS[estado] || estado}</span>;
}

function Simulacion({ simulacion }) {
  if (!simulacion) return null;
  const detalle = (simulacion.detalle || []).slice(0, 50);
  return (
    <section className="pagos-demo-dashboard" aria-label="Simulación antes y después">
      <div className="pagos-section-heading"><div><h3>Antes / después</h3>
        <p>Importación {simulacion.importacionId} · snapshot {simulacion.snapshotArchivo} · motor {simulacion.motorVersion}</p></div>
        <span className="pagos-mode-badge" title={simulacion.huella}>Huella {String(simulacion.huella).slice(0, 12)}</span></div>
      <div className="pagos-summary pagos-summary--demo">
        <div className="pagos-summary-card"><span>Operaciones</span><strong>{simulacion.operaciones}</strong><small>{simulacion.operacionesAfectadas} afectadas por la regla</small></div>
        <div className="pagos-summary-card"><span>Incompletas</span><strong>{simulacion.incompletasAntes} → {simulacion.incompletasDespues}</strong>
          <small>{simulacion.completadasPorLaRegla} completadas · {simulacion.incompletadasPorLaRegla} nuevas incompletas</small></div>
        <div className="pagos-summary-card"><span>Neto antes</span><strong>{formatDecimal(simulacion.netoAntes)}</strong><small>No se suma si hay incompletas</small></div>
        <div className="pagos-summary-card"><span>Neto después</span><strong>{formatDecimal(simulacion.netoDespues)}</strong><small>Diferencia {formatDecimal(simulacion.diferencia)}</small></div>
        <div className="pagos-summary-card"><span>Comparables</span><strong>{simulacion.operacionesComparables}</strong>
          <small>{formatDecimal(simulacion.netoAntesComparables)} → {formatDecimal(simulacion.netoDespuesComparables)}</small></div>
      </div>
      {(simulacion.reglasBase || []).length > 0 && <p className="pagos-cell-note">Antes incluye las reglas aprobadas para pruebas: {simulacion.reglasBase.join(" · ")}</p>}

      <h4 className="pagos-subheading">Por comercio</h4>
      <div className="pagos-table-wrap"><table aria-label="Impacto por comercio"><thead><tr>
        <th>CUIT</th><th>Comercio</th><th>Operaciones</th><th>Neto antes</th><th>Neto después</th><th>Diferencia</th>
      </tr></thead><tbody>
        {!(simulacion.porComercio || []).length ? <tr><td colSpan="6">La regla no cambia ninguna operación del lote.</td></tr>
          : simulacion.porComercio.map((item) => (
            <tr key={item.cuit}><td>{item.cuit}</td><td>{item.nombreComercio}</td><td>{item.operaciones}</td>
              <td>{formatDecimal(item.netoAntes)}</td><td>{formatDecimal(item.netoDespues)}</td><td>{formatDecimal(item.diferencia)}</td></tr>
          ))}
      </tbody></table></div>

      <h4 className="pagos-subheading">Operaciones afectadas{simulacion.detalleTruncado || (simulacion.detalle || []).length > detalle.length ? ` (primeras ${detalle.length})` : ""}</h4>
      <div className="pagos-table-wrap"><table aria-label="Operaciones afectadas"><thead><tr>
        <th>Operación</th><th>Fecha</th><th>Tarjeta</th><th>Bruto</th><th>Neto antes</th><th>Neto después</th><th>Componentes</th>
      </tr></thead><tbody>
        {detalle.map((op) => (
          <tr key={op.operacionId}>
            <td><strong>{op.operacionId}</strong><small className="pagos-cell-note">Terminal {op.terminal}</small></td>
            <td>{formatFecha(op.fechaOperacion)}</td><td>{op.tarjeta} · {op.cuotas} c.</td><td>{formatDecimal(op.bruto)}</td>
            <td>{op.estadoAntes === "INCOMPLETO" ? "Incompleta" : formatDecimal(op.netoAntes)}</td>
            <td>{op.estadoDespues === "INCOMPLETO" ? `Incompleta: ${(op.incompletitudesDespues || []).join(" ")}` : formatDecimal(op.netoDespues)}</td>
            <td><small>{(op.componentes || []).map((c) => (
              <span key={c.codigo} className="pagos-cell-note">{c.codigo}: {c.tasaAntes ?? "—"} → {c.tasaDespues ?? "—"} ({c.reglaAntes || "sin regla"} → {c.reglaDespues || "sin regla"}) · {formatDecimal(c.importeAntes, { decimales: 2 })} → {formatDecimal(c.importeDespues, { decimales: 2 })}</span>
            ))}</small></td>
          </tr>
        ))}
      </tbody></table></div>
    </section>
  );
}

export default function CondicionesFinancieras({ token }) {
  const [estado, setEstado] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotId, setSnapshotId] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [comercios, setComercios] = useState([]);
  const [comercio, setComercio] = useState(null);
  const [historica, setHistorica] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [reglas, setReglas] = useState([]);
  const [seleccionada, setSeleccionada] = useState(null);
  const [historial, setHistorial] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [modo, setModo] = useState("crear");
  const [fecha, setFecha] = useState("");
  const [importaciones, setImportaciones] = useState([]);
  const [importacionId, setImportacionId] = useState(null);
  const [simulacion, setSimulacion] = useState(null);
  const [comentario, setComentario] = useState("");
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  const ejecutar = useCallback(async (clave, accion) => {
    setError(""); setMensaje(""); setLoading(clave);
    try {
      return await accion();
    } catch (requestError) {
      setError(requestError?.message || "No se pudo completar la operación.");
      return null;
    } finally {
      setLoading("");
    }
  }, []);

  const cargarReglas = useCallback(() => ejecutar("reglas", async () => {
    setReglas(await pagosApi.listarReglas({ estado: filtroEstado }, token) || []);
  }), [ejecutar, filtroEstado, token]);

  useEffect(() => {
    let vigente = true;
    Promise.resolve().then(() => pagosApi.estadoCondiciones(token))
      .then(async (response) => {
        if (!vigente) return;
        setEstado(response || { habilitada: false });
        if (response?.habilitada) {
          const lista = await pagosApi.snapshotsCondiciones(token);
          if (!vigente) return;
          setSnapshots(lista || []);
          setSnapshotId(lista?.[0]?.id || "");
        }
      })
      .catch((requestError) => { if (vigente) setEstado({ habilitada: false, motivo: requestError?.message }); });
    return () => { vigente = false; };
  }, [token]);

  useEffect(() => { if (estado?.habilitada) cargarReglas(); }, [estado, cargarReglas]);

  const tipos = useMemo(() => estado?.tipos || [], [estado]);
  const tipo = tipos.find((item) => item.tipo === form.tipo);
  const alcances = tipo ? Object.keys(tipo.alcances || {}) : [];
  const parametros = (tipo?.alcances || {})[form.alcance] || [];
  const usuarioId = estado?.usuario?.id;

  const actualizar = (campo, valor) => setForm((actual) => {
    const next = { ...actual, [campo]: valor };
    if (campo === "tipo") { next.alcance = ""; next.parametro = ""; }
    if (campo === "alcance") {
      next.parametro = "";
      next.sujeto = valor === "GENERAL" ? "*" : valor === "COMERCIO" ? comercio?.cuit || "" : "";
    }
    return next;
  });

  const cargarSnapshot = (file) => file && ejecutar("snapshot", async () => {
    const creado = await pagosApi.cargarSnapshotCondiciones(file, token);
    const lista = await pagosApi.snapshotsCondiciones(token);
    setSnapshots(lista || []);
    setSnapshotId(creado.id);
    setMensaje(creado.creado ? `Snapshot cargado: ${creado.archivo}.` : `El libro ya estaba cargado (${creado.archivo}).`);
  });

  const buscar = () => ejecutar("comercios", async () => {
    setComercios(await pagosApi.buscarComercios(busqueda, token) || []);
  });

  const elegirComercio = (item) => ejecutar("historica", async () => {
    setComercio(item);
    setForm((actual) => (actual.alcance === "COMERCIO" ? { ...actual, sujeto: item.cuit } : actual));
    setHistorica(await pagosApi.condicionComercio(item.cuit, snapshotId, token));
  });

  const seleccionar = (regla) => ejecutar("detalle", async () => {
    const detalle = await pagosApi.obtenerRegla(regla.id, token);
    setSeleccionada(detalle);
    setSimulacion(detalle?.simulacion || null);
    setHistorial(await pagosApi.historialRegla(detalle.raizId, token));
    setForm({
      tipo: detalle.tipo, alcance: detalle.alcance, sujeto: detalle.sujeto, parametro: detalle.parametro, prioridad: String(detalle.prioridad),
      porcentaje: String(detalle.porcentaje).replace(".", ","), desde: fechaInput(detalle.desde), hasta: fechaInput(detalle.hasta), fuente: detalle.fuente, motivo: detalle.motivo,
    });
    const editable = ["BORRADOR", "SIMULADO"].includes(detalle.estado) && detalle.creadoPorUsuarioId === usuarioId;
    setModo(editable ? "editar" : ["APROBADO_PARA_PRUEBAS", "RECHAZADO"].includes(detalle.estado) ? "version" : "ver");
  });

  const refrescar = async (regla, texto) => {
    await cargarReglas();
    if (regla) await seleccionar(regla);
    setMensaje(texto);
  };

  const guardar = () => ejecutar("guardar", async () => {
    const body = { ...form, prioridad: Number.parseInt(form.prioridad || "0", 10), hasta: form.hasta || null };
    const regla = modo === "editar" ? await pagosApi.editarRegla(seleccionada.id, body, token)
      : modo === "version" ? await pagosApi.nuevaVersionRegla(seleccionada.raizId, body, token)
        : await pagosApi.crearRegla(body, token);
    await refrescar(regla, modo === "editar" ? "Borrador actualizado: volvé a simular." : `Borrador v${regla.version} creado.`);
  });

  const nueva = () => { setSeleccionada(null); setHistorial(null); setSimulacion(null); setModo("crear"); setForm({ ...FORM_VACIO, sujeto: comercio?.cuit || "" }); };

  const buscarLotes = () => ejecutar("lotes", async () => {
    const response = await pagosApi.lotesSas(fecha, token);
    setImportaciones((response?.importaciones || []).filter((item) => item.aptaSas));
    setImportacionId(response?.importacionSugerida ?? null);
  });

  const simular = () => ejecutar("simular", async () => {
    const response = await pagosApi.simularRegla(seleccionada.id, { importacionId, snapshotId }, token);
    setSimulacion(response.simulacion);
    await refrescar(response.regla, "Simulación registrada.");
    setSimulacion(response.simulacion);
  });

  const transicion = (accion) => ejecutar(accion, async () => {
    const regla = accion === "enviar" ? await pagosApi.enviarRegla(seleccionada.id, token)
      : accion === "aprobar" ? await pagosApi.aprobarRegla(seleccionada.id, comentario, token)
        : await pagosApi.rechazarRegla(seleccionada.id, comentario, token);
    setComentario("");
    await refrescar(regla, `Regla ${ESTADO_LABELS[regla.estado].toLowerCase()}.`);
  });

  if (!estado) return <section className="pagos-panel" data-testid="condiciones-financieras"><p>Cargando condiciones…</p></section>;
  if (!estado.habilitada) {
    return (
      <section className="pagos-panel" data-testid="condiciones-financieras">
        <h2>Condiciones financieras</h2>
        <div className="pagos-alert pagos-alert--warning" role="status">El catálogo sólo funciona en el entorno aislado de desarrollo. {estado.motivo}</div>
      </section>
    );
  }

  const propia = seleccionada && (seleccionada.creadoPorUsuarioId === usuarioId || seleccionada.enviadoPorUsuarioId === usuarioId);
  const editableForm = modo !== "ver";

  return (
    <section className="pagos-panel" aria-labelledby="pagos-condiciones-title" data-testid="condiciones-financieras">
      <div className="pagos-section-heading">
        <div>
          <h2 id="pagos-condiciones-title">Condiciones financieras</h2>
          <p>Catálogo versionado para pruebas. Proponé, simulá con el motor y enviá a aprobación de un segundo usuario.</p>
        </div>
        <span className="pagos-status pagos-status--safe">Desarrollo aislado · {estado.baseDatos}</span>
      </div>
      <div className="pagos-alert pagos-alert--info" role="note">
        Las reglas aprobadas lo son <strong>sólo para pruebas</strong>: no cambian tasas productivas, no liquidan ni pagan. Una tasa faltante nunca se completa con 0 %: se propone, se respalda con fuente y se aprueba.
      </div>
      {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}
      {mensaje && <div className="pagos-alert" role="status">{mensaje}</div>}

      <div className="pagos-condiciones-grid">
        <div>
          <h3 className="pagos-subheading">1 · Parámetros de referencia</h3>
          <label className="pagos-date"><span>Snapshot de Listas</span>
            <select aria-label="Snapshot de parámetros" value={snapshotId} onChange={(event) => setSnapshotId(event.target.value)}>
              {!snapshots.length && <option value="">Sin snapshots cargados</option>}
              {snapshots.map((item) => <option key={item.id} value={item.id}>{item.archivo} · {String(item.sha256).slice(0, 10)}</option>)}
            </select>
          </label>
          <label className="pagos-date"><span>Cargar Diario (.xlsx/.xlsm, sólo valores)</span>
            <input aria-label="Libro con hoja Listas" type="file" accept=".xlsx,.xlsm" disabled={loading === "snapshot"}
              onChange={(event) => cargarSnapshot(event.target.files?.[0])} />
          </label>

          <h3 className="pagos-subheading">2 · Comercio</h3>
          <div className="pagos-filter-grid">
            <input className="pagos-search" aria-label="Buscar comercio" placeholder="CUIT o nombre" value={busqueda} onChange={(event) => setBusqueda(event.target.value)} />
            <button type="button" className="pagos-secondary" disabled={busqueda.trim().length < 3 || loading === "comercios"} onClick={buscar}>Buscar</button>
          </div>
          {comercios.length > 0 && <ul className="pagos-run-list" aria-label="Comercios encontrados">
            {comercios.map((item) => (
              <li key={item.cuit}><button type="button" className="pagos-secondary" onClick={() => elegirComercio(item)}>
                {item.nombreComercio || item.razonSocial} · {item.cuit}{item.soloBajas ? " · dado de baja" : ""}</button></li>
            ))}
          </ul>}
          {historica && <div className="pagos-reconciliation" aria-label="Condición histórica">
            <h4>Condición histórica · {comercio?.nombreComercio} ({historica.cuit})</h4>
            {historica.condicionListas
              ? <p>Listas {historica.condicionListas.origen}: preferencial {historica.condicionListas.preferencial ? `sí desde ${formatFecha(historica.condicionListas.preferencialDesde)}` : "no"} ·
                débito/crédito propio {historica.condicionListas.debitoCreditoPropio ? `sí desde ${formatFecha(historica.condicionListas.debitoCreditoDesde)} · ${historica.condicionListas.debitoCreditoPorcentaje === null ? "sin porcentaje (incompleto)" : `${String(historica.condicionListas.debitoCreditoPorcentaje).replace(".", ",")} (fracción)`}` : "no"}</p>
              : <p>Sin condición particular en Listas.</p>}
            {historica.bonificacionListas
              ? <p>Bonificación {historica.bonificacionListas.origen} desde {formatFecha(historica.bonificacionListas.desde)} ({historica.bonificacionListas.activa ? "activa" : "inactiva"}).</p>
              : <p>Sin bonificación en Listas.</p>}
            <p>{(historica.reglas || []).length} reglas en el catálogo para este CUIT.</p>
          </div>}
        </div>

        <div>
          <h3 className="pagos-subheading">3 · {modo === "editar" ? `Editar v${seleccionada.version}` : modo === "version" ? `Nueva versión de v${seleccionada.version}` : modo === "ver" ? `Regla v${seleccionada.version}` : "Nueva regla"}</h3>
          <div className="pagos-form-grid">
            <label><span>Tipo de regla</span>
              <select aria-label="Tipo de regla" value={form.tipo} disabled={!editableForm || modo === "version"} onChange={(event) => actualizar("tipo", event.target.value)}>
                <option value="">Elegí…</option>
                {tipos.map((item) => <option key={item.tipo} value={item.tipo}>{item.tipo} · {item.descripcion}</option>)}
              </select></label>
            <label><span>Alcance</span>
              <select aria-label="Alcance" value={form.alcance} disabled={!editableForm || modo === "version"} onChange={(event) => actualizar("alcance", event.target.value)}>
                <option value="">Elegí…</option>
                {alcances.map((item) => <option key={item} value={item}>{item}</option>)}
              </select></label>
            <label><span>Sujeto</span>
              <input aria-label="Sujeto" value={form.sujeto} disabled={!editableForm || modo === "version" || form.alcance === "GENERAL"}
                placeholder={form.alcance === "OPERACION" ? "imp1180-f181563" : "CUIT"} onChange={(event) => actualizar("sujeto", event.target.value)} /></label>
            <label><span>Parámetro</span>
              {parametros.includes("CUOTAS:N")
                ? <input aria-label="Parámetro" type="number" min="0" max="99" value={form.parametro} disabled={!editableForm || modo === "version"} placeholder="Cuotas (0 = débito)" onChange={(event) => actualizar("parametro", event.target.value)} />
                : <select aria-label="Parámetro" value={form.parametro} disabled={!editableForm || modo === "version"} onChange={(event) => actualizar("parametro", event.target.value)}>
                  <option value="">Elegí…</option>
                  {parametros.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>}</label>
            <label><span>Porcentaje propuesto {tipo ? `(${tipo.minimoPorcentaje}–${tipo.maximoPorcentaje} %)` : ""}</span>
              <input aria-label="Porcentaje" inputMode="decimal" value={form.porcentaje} disabled={!editableForm} placeholder="1,20" onChange={(event) => actualizar("porcentaje", event.target.value)} /></label>
            <label><span>Prioridad</span>
              <input aria-label="Prioridad" type="number" min="0" max="100" value={form.prioridad} disabled={!editableForm || modo === "version"} onChange={(event) => actualizar("prioridad", event.target.value)} /></label>
            <label><span>Vigente desde</span>
              <input aria-label="Vigente desde" type="date" value={form.desde} disabled={!editableForm} onChange={(event) => actualizar("desde", event.target.value)} /></label>
            <label><span>Vigente hasta (opcional)</span>
              <input aria-label="Vigente hasta" type="date" value={form.hasta} disabled={!editableForm} onChange={(event) => actualizar("hasta", event.target.value)} /></label>
            <label className="pagos-form-wide"><span>Fuente que respalda la tasa</span>
              <input aria-label="Fuente" value={form.fuente} disabled={!editableForm} placeholder="Acuerdo, certificado o celda" onChange={(event) => actualizar("fuente", event.target.value)} /></label>
            <label className="pagos-form-wide"><span>Motivo</span>
              <textarea aria-label="Motivo" value={form.motivo} disabled={!editableForm} onChange={(event) => actualizar("motivo", event.target.value)} /></label>
          </div>
          <div className="pagos-filter-grid">
            <button type="button" className="pagos-secondary" onClick={nueva}>Nueva regla</button>
            {editableForm && <button type="button" className="pagos-primary" disabled={loading === "guardar"} onClick={guardar}>
              {modo === "editar" ? "Guardar cambios" : modo === "version" ? "Crear versión" : "Guardar borrador"}</button>}
          </div>
        </div>
      </div>

      {seleccionada && <section className="pagos-reconciliation" aria-label="Simulación y aprobación">
        <h3>4 · Simulación y aprobación · <Estado estado={seleccionada.estado} /></h3>
        <p className="pagos-cell-note">Propuso {seleccionada.creadoPor}{seleccionada.enviadoPor ? ` · envió ${seleccionada.enviadoPor}` : ""}{seleccionada.resueltoPor ? ` · resolvió ${seleccionada.resueltoPor}: ${seleccionada.comentarioResolucion || "sin comentario"}` : ""}</p>
        {["BORRADOR", "SIMULADO"].includes(seleccionada.estado) && <div className="pagos-filter-grid">
          <label className="pagos-date"><span>Fecha del lote SAS</span>
            <input aria-label="Fecha del lote a simular" type="date" value={fecha} onChange={(event) => setFecha(event.target.value)} /></label>
          <button type="button" className="pagos-secondary" disabled={!fecha || loading === "lotes"} onClick={buscarLotes}>Buscar importación</button>
          <select aria-label="Importación a simular" value={importacionId ?? ""} onChange={(event) => setImportacionId(Number(event.target.value) || null)}>
            <option value="">Importación…</option>
            {importaciones.map((item) => <option key={item.id} value={item.id}>{item.id} · {item.archivo} · {item.filasInsertadas} filas</option>)}
          </select>
          <button type="button" className="pagos-primary" disabled={!importacionId || !snapshotId || loading === "simular"} onClick={simular}>
            {loading === "simular" ? "Simulando…" : "Simular con el motor"}</button>
        </div>}
        {seleccionada.estado === "SIMULADO" && !propia && <p className="pagos-cell-note">La envía a aprobación quien la propuso.</p>}
        {seleccionada.estado === "SIMULADO" && propia && <button type="button" className="pagos-primary" disabled={loading === "enviar"} onClick={() => transicion("enviar")}>Enviar a aprobación</button>}
        {seleccionada.estado === "PENDIENTE_APROBACION" && (propia
          ? <p className="pagos-cell-note">Quien propone una regla no puede aprobarla ni rechazarla: la resuelve otro usuario.</p>
          : <div className="pagos-filter-grid">
            <input aria-label="Comentario de resolución" className="pagos-search" placeholder="Comentario (obligatorio para rechazar)" value={comentario} onChange={(event) => setComentario(event.target.value)} />
            <button type="button" className="pagos-primary" disabled={loading === "aprobar"} onClick={() => transicion("aprobar")}>Aprobar para pruebas</button>
            <button type="button" className="pagos-secondary" disabled={loading === "rechazar" || !comentario.trim()} onClick={() => transicion("rechazar")}>Rechazar</button>
          </div>)}
        <Simulacion simulacion={simulacion} />
      </section>}

      <h3 className="pagos-subheading">Catálogo</h3>
      <div className="pagos-filter-grid">
        <select aria-label="Filtrar por estado" value={filtroEstado} onChange={(event) => setFiltroEstado(event.target.value)}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </div>
      <div className="pagos-table-wrap"><table aria-label="Condiciones del catálogo"><thead><tr>
        <th>Tipo</th><th>Alcance · sujeto</th><th>Parámetro</th><th>Tasa</th><th>Vigencia</th><th>Versión</th><th>Estado</th><th>Propuso</th><th></th>
      </tr></thead><tbody>
        {!reglas.length ? <tr><td colSpan="9"><div className="pagos-empty"><p>No hay reglas en el catálogo.</p></div></td></tr>
          : reglas.map((regla) => (
            <tr key={regla.id} className={seleccionada?.id === regla.id ? "pagos-row--review" : ""}>
              <td>{regla.tipo}</td><td>{regla.alcance} · {regla.sujeto}</td><td>{regla.parametro}</td><td>{formatPorcentaje(regla.porcentaje)}</td>
              <td>{vigencia(regla)}</td><td>v{regla.version}</td><td><Estado estado={regla.estado} /></td><td>{regla.creadoPor}</td>
              <td><button type="button" className="pagos-secondary" onClick={() => seleccionar(regla)}>Ver</button></td>
            </tr>
          ))}
      </tbody></table></div>

      {historial && <>
        <h3 className="pagos-subheading">Historial de la regla</h3>
        <div className="pagos-table-wrap"><table aria-label="Historial de revisiones"><thead><tr>
          <th>Fecha</th><th>Versión</th><th>Acción</th><th>Estado</th><th>Usuario</th><th>Tasa</th><th>Comentario</th>
        </tr></thead><tbody>
          {(historial.revisiones || []).map((item) => (
            <tr key={item.id}><td>{String(item.fecha).replace("T", " ").slice(0, 16)}</td><td>v{item.version}</td><td>{item.accion}</td>
              <td>{item.estadoAnterior ? `${ESTADO_LABELS[item.estadoAnterior] || item.estadoAnterior} → ` : ""}{ESTADO_LABELS[item.estadoNuevo] || item.estadoNuevo}</td>
              <td>{item.usuario}</td><td>{item.contenido?.valor}</td><td>{item.comentario}</td></tr>
          ))}
        </tbody></table></div>
      </>}
    </section>
  );
}
